import { Document, Page, pdf, Text, View } from "@react-pdf/renderer"
import { styles, TableCell } from "components"
import moment from "moment"
import { cellValuePdf, RowExportTypePdf } from "types"
import { convertNumber } from "utils"
import { CardType } from "../../components/TabByCard/types"
import { APPROVAL, DELIVERED } from "../vars"

type UseExportPdfByCard = {
  type: "REGISTER BY CARD" | "REGISTER BY LOCATION" | "REFERENCE ID"
  data: CardType[]
  dateFilter: string
}

export const useExportPdfByCard = ({
  type,
  data,
  dateFilter
}: UseExportPdfByCard) => {
  const isByCard = type === "REGISTER BY CARD"
  const isByLocation = type === "REGISTER BY LOCATION"

  const columns = [
    { header: "NO", key: "no", width: 5 },
    { header: "SO ID#", key: "so_id", width: 25 },
    { header: "DATE", key: "date", width: 15 },
    isByCard
      ? { header: "CARD", key: "card", width: 15 }
      : { header: "CUSTOMER", key: "trader", width: 15 },
    isByCard
      ? { header: "TRADER", key: "trader", width: 15 }
      : isByLocation
        ? { header: "LOCATION", key: "location", width: 15 }
        : { header: "REFERENCE", key: "location", width: 15 },
    { header: "APPROVAL", key: "approval", width: 15 },
    { header: "DELIVERED", key: "delivered", width: 15 },
    { header: "QTY ORDER", key: "qty_order", width: 15 },
    { header: "QTY DELIVERED", key: "qty_delivered", width: 15 },
    { header: "QTY BACK ORDER", key: "qty_back_order", width: 15 },
    { header: "TOTAL SALES", key: "total_sales", width: 15 },
    { header: "TOTAL PAYMENT", key: "total_payment", width: 15 },
    { header: "BALANCE OWING", key: "balance_owing", width: 15 }
  ]

  function numberFormat(value: number): cellValuePdf {
    return {
      text: convertNumber(value).intoCurrency,
      style: {
        textAlign: "right",
        fontWeight: "bold"
      }
    }
  }

  const calculate = data.reduce(
    (acc, item) => {
      return {
        balance: acc.balance + item.balance_owing,
        payment: acc.payment + item.total_payment
      }
    },
    { balance: 0, payment: 0 }
  )

  const dataMap: RowExportTypePdf[] = data.map((item, index) => {
    return {
      no: (index + 1).toString(),
      so_id: item.trx_code,
      date: moment(item.transaction_date).format("DD/MM/YYYY"),
      card: "CUSTOMER",
      location: item.location.location_name,
      trader: item.customer.customer_name,
      approval: APPROVAL[item.approval_status],
      delivered: DELIVERED[item.delivery_status],
      qty_order: numberFormat(item.qty_order),
      qty_delivered: numberFormat(item.qty_delivered),
      qty_back_order: numberFormat(item.qty_back_order),
      total_sales: numberFormat(item.total_sales),
      total_payment: numberFormat(item.total_payment),
      balance_owing: numberFormat(item.balance_owing)
    }
  })

  return {
    handleExportPdf: () =>
      pdf(
        <PdfTemplate
          data={dataMap}
          title={type}
          column={columns}
          date={dateFilter}
          balance={calculate.balance}
          payment={calculate.payment}
        />
      )
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement("a")
          const name = `register-${isByCard ? "by-card" : isByLocation ? "by-location" : "reference"}`
          link.href = url
          link.download = `${name}-${dateFilter}.pdf`
          link.click()
          URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error("Gagal membuat PDF:", error)
        })
  }
}

function PdfTemplate({
  data,
  title,
  column,
  date,
  balance,
  payment
}: {
  data: RowExportTypePdf[]
  title: string
  column: { header: string; key: string; width: number }[]
  date?: string
  balance: number
  payment: number
}) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>SALES</Text>
        <Text style={styles.title}>{title}</Text>
        {date && <Text style={styles.date}>Date: {date}</Text>}
        <TableCell data={data} column={column} />
        <View
          style={{
            marginTop: 10,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-end"
          }}>
          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY PAYMENT:</Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(payment).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY BALANCE:</Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(balance).intoCurrency}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  )
}
